import { FC, useContext, useMemo } from 'react';
import { Button, Card, Skeleton, Space, Statistic, Tooltip } from 'antd';
import * as Icons from '@ant-design/icons';
import { SettingsContext } from '@modules/settings';
import { CurrencySymbols } from '@shared/utils';
import * as UserWidget from '../components/user-widget';
import { useUserWidget, useUserAddress } from '../hooks';

export const UserCell: FC<{ id: number | string; title: string }> = ({ id, title }) => {
  const settings = useContext(SettingsContext);
  const cardSize = useMemo(() => ({ height: 377, width: 517 }), []);

  const {
    visible,
    setVisible,
    data,
    isLoading,
    openDeclarations,
    openCurrentMonthDeclarations,
    openOrders,
    openCouriers,
    openTransactions,
    openCustomsDeclarations,
    openTickets,
    openTicketsForm,
    openWhatsapp,
  } = useUserWidget(id);

  const [copyAddress] = useUserAddress(data);

  const extra = (
    <Space.Compact>
      <UserWidget.Link onClick={openTickets}>Müraciətlər</UserWidget.Link>
      <UserWidget.Link onClick={openTicketsForm} icon={<Icons.PlusCircleOutlined />} />
    </Space.Compact>
  );

  const loading = (
    <UserWidget.Card style={cardSize}>
      <Skeleton avatar title paragraph={{ rows: 0 }} />
      <Space>
        <Skeleton.Button />
        <Skeleton.Button />
        <Skeleton.Button />
        <Skeleton.Button />
      </Space>
      <Skeleton paragraph={{ rows: 7 }} />
    </UserWidget.Card>
  );

  const transactionCounts = (
    <div>
      <div>
        <b>Mədaxil sayı: </b> {data?.counts.transactions.income}
      </div>
      <div>
        <b>Məxaric sayı: </b> {data?.counts.transactions.outcome}
      </div>
    </div>
  );

  const courierCounts = (
    <div>
      <div>
        <b>Ümumi say: </b> {data?.counts.couriers.all}
      </div>
      <div>
        <b>Təhvil verilmiş: </b> {data?.counts.couriers.handedOver}
      </div>
    </div>
  );

  const orderCounts = (
    <div>
      <div>
        <b>Ümumi say: </b> {data?.counts.orders.all}
      </div>
      <div>
        <b>Təhvil verilmiş: </b> {data?.counts.orders.handedOver}
      </div>
    </div>
  );

  const declarationCounts = (
    <div>
      <div>
        <b>Ümumi say: </b> {data?.counts.declarations.all}
      </div>
      <div>
        <b>Təhvil verilmiş: </b> {data?.counts.declarations.handedOver}
      </div>
    </div>
  );

  const content = !!data && (
    <UserWidget.Card style={cardSize} title={`#${data.id} - ${data.fullName}`} extra={extra}>
      <UserWidget.Balances size="small">
        <Card size="small">
          <Statistic title="Balans" value={data.balance.usd} prefix={CurrencySymbols.USD} precision={2} />
        </Card>
        <Card size="small">
          <Statistic title="Balans" value={data.balance.try} prefix={CurrencySymbols.TRY} precision={2} />
        </Card>
        <Card size="small">
          <Statistic title="Borc" value={data.debt.try} prefix={CurrencySymbols.TRY} precision={2} />
        </Card>
      </UserWidget.Balances>
      <UserWidget.Links>
        <Tooltip placement="bottom" title={orderCounts}>
          <UserWidget.Link onClick={openOrders}>Sifarişlər</UserWidget.Link>
        </Tooltip>
        <Tooltip placement="bottom" title={declarationCounts}>
          <UserWidget.Link onClick={openDeclarations}>Bağlamalar</UserWidget.Link>
        </Tooltip>
        <Tooltip placement="bottom" title={courierCounts}>
          <UserWidget.Link onClick={openCouriers}>Kuryerlər</UserWidget.Link>
        </Tooltip>
        <Tooltip placement="bottom" title={transactionCounts}>
          <UserWidget.Link onClick={openTransactions}>Balans əməliyyatları</UserWidget.Link>
        </Tooltip>
        <UserWidget.Link onClick={openCustomsDeclarations}>DGK məlumatları</UserWidget.Link>
      </UserWidget.Links>
      <UserWidget.Descriptions size="small" bordered column={1}>
        <UserWidget.Descriptions.Item label="Ünvan">
          <Space wrap>
            {(settings.warehouse || []).map((wh) => (
              <Tooltip key={wh.id} title={wh.address_heading || wh.country_name}>
                <Button onClick={() => copyAddress(wh.address)} size="small" type="link" icon={<Icons.CopyOutlined />}>
                  {wh.country_name}
                </Button>
              </Tooltip>
            ))}
          </Space>
        </UserWidget.Descriptions.Item>
        <UserWidget.Descriptions.Item label="Cari ay">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space size={8}>
              <span>
                {data.spending.currentMonth.usd.toFixed(2)}
                {CurrencySymbols.USD} - {data.spending.currentMonth.try.toFixed(2)}
                {CurrencySymbols.TRY}
              </span>
              {data.spending.currentMonth.usd > 300 ? <Icons.CloseCircleOutlined style={{ color: '#cf1322' }} /> : <Icons.CheckCircleOutlined style={{ color: '#52c41a' }} />}
            </Space>
            <Button onClick={openCurrentMonthDeclarations} size="small" icon={<Icons.OrderedListOutlined />}>
              Bağlamalar
            </Button>
          </div>
        </UserWidget.Descriptions.Item>
        <UserWidget.Descriptions.Item label="Telefon">
          <Space size={8}>
            <span>{data.phoneNumber}</span>
            <Icons.WhatsAppOutlined onClick={openWhatsapp} style={{ color: '#52c41a', cursor: 'pointer' }} />
          </Space>
        </UserWidget.Descriptions.Item>
        <UserWidget.Descriptions.Item label="Ş.V nömrəsi">{data.passport.number}</UserWidget.Descriptions.Item>
      </UserWidget.Descriptions>
    </UserWidget.Card>
  );

  return (
    <UserWidget.PopoverOuter>
      <UserWidget.Popover trigger={['hover']} placement="right" onOpenChange={setVisible} open={visible} content={data && !isLoading ? content : loading}>
        <UserWidget.Button size="small">{title}</UserWidget.Button>
      </UserWidget.Popover>
    </UserWidget.PopoverOuter>
  );
};
